export default {
  namespaced: true,
  
  state: {
    history: [],
    maxHistoryLength: 6, // Store 6 links
    currentIndex: -1, // Index of current active page in history
    maxVisibleHistory: 3 // Maximum number of visible links in history
  },
  
  getters: {
    // Get visible paths for display (3 latest including current)
    visiblePaths: state => {
      if (state.history.length === 0) return [];
      
      // If history has less than 3 elements, show all
      if (state.history.length <= state.maxVisibleHistory) {
        return state.history;
      }
      
      // Define center element of history as current
      const centerIndex = state.currentIndex;
      
      // Define start index (current - 1)
      let startIdx = Math.max(0, centerIndex - 1);
      
      // If start index + 3 goes beyond array bounds, shift left
      if (startIdx + state.maxVisibleHistory > state.history.length) {
        startIdx = Math.max(0, state.history.length - state.maxVisibleHistory);
      }
      
      return state.history.slice(startIdx, startIdx + state.maxVisibleHistory);
    },
    
    // Get full history
    fullHistory: state => state.history,
    
    // Get current active path
    currentPath: state => {
      if (state.currentIndex >= 0 && state.currentIndex < state.history.length) {
        return state.history[state.currentIndex];
      }
      return null;
    },
    
    // Get current index
    currentIndex: state => state.currentIndex
  },
  
  mutations: {
    // Add new path to history
    addPathToHistory(state, path) {
      // Ensure history array is initialized
      if (!state.history) {
        state.history = [];
      }
      
      // If path is the same as current - don't add
      if (state.currentIndex >= 0 && 
          state.currentIndex < state.history.length &&
          state.history[state.currentIndex].path === path.path) {
        return;
      }
      
      // Remove all paths after current if we're not at the end of history
      if (state.currentIndex >= 0 && state.currentIndex < state.history.length - 1) {
        state.history = state.history.slice(0, state.currentIndex + 1);
      }
      
      // Remove path from history if it already exists
      const existingIndex = state.history.findIndex(item => item.path === path.path);
      if (existingIndex !== -1) {
        state.history.splice(existingIndex, 1);
      }
      
      // Add new path to the end
      state.history.push(path);
      
      // Set current index to last element
      state.currentIndex = state.history.length - 1;
      
      // Limit history size
      if (state.history.length > state.maxHistoryLength) {
        const itemsToRemove = state.history.length - state.maxHistoryLength;
        state.history.splice(0, itemsToRemove);
        state.currentIndex = Math.max(0, state.currentIndex - itemsToRemove);
      }
      
      // Save to localStorage
      localStorage.setItem('navigationHistory', JSON.stringify({
        history: state.history,
        currentIndex: state.currentIndex
      }));
    },
    
    // Set current index
    setCurrentIndex(state, index) {
      // Ensure history array is initialized
      if (!state.history) {
        state.history = [];
      }
      
      if (index >= 0 && index < state.history.length) {
        state.currentIndex = index;
        
        // Save to localStorage
        localStorage.setItem('navigationHistory', JSON.stringify({
          history: state.history,
          currentIndex: state.currentIndex
        }));
      }
    },
    
    // Set current path
    setCurrentPath(state, path) {
      // Ensure history array is initialized
      if (!state.history) {
        state.history = [];
      }
      
      const index = state.history.findIndex(item => item.path === path);
      if (index !== -1) {
        state.currentIndex = index;
        
        // Save to localStorage
        localStorage.setItem('navigationHistory', JSON.stringify({
          history: state.history,
          currentIndex: state.currentIndex
        }));
      }
    },
    
    // Load history from localStorage
    loadHistory(state) {
      try {
        const savedHistory = localStorage.getItem('navigationHistory');
        if (savedHistory) {
          const { history, currentIndex } = JSON.parse(savedHistory);
          state.history = history || [];
          state.currentIndex = currentIndex || -1;
        } else {
          // Initialize with empty arrays if no saved data
          state.history = [];
          state.currentIndex = -1;
        }
      } catch (error) {
        // Initialize with empty arrays on error
        state.history = [];
        state.currentIndex = -1;
      }
    },
    
    // Clear history
    clearHistory(state) {
      state.history = [];
      state.currentIndex = -1;
      localStorage.removeItem('navigationHistory');
    }
  },
  
  actions: {
    // Initialize history on app startup
    initHistory({ commit }) {
      commit('loadHistory');
    },
    
    // Add new path to history
    addToHistory({ commit, state }, routeData) {
      const pathInfo = {
        path: routeData.path,
        name: routeData.name || routeData.path,
        meta: routeData.meta || {}
      };
      
      commit('addPathToHistory', pathInfo);
    },
    
    // Navigate through history to specific path
    navigateToPath({ commit, state }, path) {
      // Ensure history array is initialized
      if (!state.history) {
        state.history = [];
      }
      
      const index = state.history.findIndex(item => item.path === path);
      if (index !== -1) {
        commit('setCurrentIndex', index);
        return state.history[index];
      }
      return null;
    },
    
    // Reset history
    resetHistory({ commit }) {
      commit('clearHistory');
    }
  }
}; 